import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Role → dashboard path mapping
const ROLE_PATHS = {
  team:        '/scaling/team',
  coach:       '/scaling/coach',
  beneficiary: '/scaling/company',
  client:      '/scaling/client',
};

export default function LoginPage() {
  const { login, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // Auth state change will update profile; redirect handled in useEffect below
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Redirect once profile is loaded after login
  React.useEffect(() => {
    if (profile?.role) {
      const dest = from && from !== '/scaling/login'
        ? from
        : ROLE_PATHS[profile.role] || '/scaling';
      navigate(dest, { replace: true });
    }
  }, [profile, navigate, from]);

  return (
    <div className="gm-login-page">
      {/* Left panel — brand */}
      <div className="gm-login-brand">
        <div className="gm-login-brand-inner">
          <div className="gm-login-logos">
            <img src="/logos/gm-logo.png" alt="Growth Mosaic" className="gm-logo-main" />
          </div>
          <h1 className="gm-login-title">Growth Mosaic</h1>
          <p className="gm-login-subtitle">Enterprise Support Platform</p>
          <div className="gm-login-module-badge">
            <span>Scaling Readiness Module</span>
          </div>
          <div className="gm-login-tagline">
            <p>Supporting early-stage businesses to optimise, grow, and scale.</p>
          </div>
        </div>
        <div className="gm-login-powered">
          <span>Powered by</span>
          <img src="/logos/mirepa-logo.png" alt="Mirepa" className="gm-logo-mirepa" />
          <div className="gm-powered-by-text">Part of the Mirepa Platform</div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="gm-login-form-panel">
        <div className="gm-login-form-inner">
          <h2>Sign in</h2>
          <p className="gm-login-form-sub">
            Use the email address your programme coordinator shared with you.
          </p>

          <form onSubmit={handleSubmit} className="gm-login-form">
            {error && (
              <div className="gm-alert gm-alert-error" role="alert">
                {error}
              </div>
            )}

            <div className="gm-form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@organisation.com"
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="gm-form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="gm-btn gm-btn-primary gm-btn-full"
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="gm-login-help">
            Trouble signing in? Contact your programme coordinator.
          </p>
        </div>
      </div>
    </div>
  );
}
